import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, ChevronRight, ChevronLeft, UploadCloud, X, MessageCircle } from "lucide-react";

function fileToDataUrl(file) {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
}

async function compressImg(dataUrl, maxSide = 1000, quality = 0.85) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      const s = Math.min(1, maxSide / Math.max(img.width, img.height));
      c.width = Math.round(img.width * s); c.height = Math.round(img.height * s);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

function StepDot({ n, label, active }) {
  return (
    <div className={`flex items-center gap-2 ${active ? "text-jpa-primary" : "text-jpa-muted"}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${active ? "bg-jpa-primary text-white" : "bg-jpa-surface"}`}>{n}</div>
      <span className="text-[10px] uppercase tracking-widest font-bold hidden md:inline">{label}</span>
    </div>
  );
}

export default function JoinTournamentPage() {
  const { id: tid } = useParams();
  const { user, loading, refreshMe } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [step, setStep] = useState(0);   // 0 = login gate, 1..4 wizard, 5 = success
  const [picks, setPicks] = useState({});
  const [whatsapp, setWhatsapp] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  // Inline login (JPA ID + password, no google, no email)
  const [loginJpaId, setLoginJpaId] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);

  const loadInfo = () => api.get(`/tournaments/${tid}/registration-info`)
    .then((r) => { setInfo(r.data); setStep(1); })
    .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); nav(`/tournaments/${tid}`); });

  useEffect(() => {
    if (loading) return;
    if (!user) { setStep(0); return; }
    if (user.role !== "player") { toast.error("Only approved JPA players can join tournaments"); nav(`/tournaments/${tid}`); return; }
    if (!user.jpa_id) { toast.error("Your JPA ID has not been issued yet"); nav("/dashboard"); return; }
    setWhatsapp(user.phone || "");
    loadInfo();
  }, [user, loading, tid, nav]);

  const doJpaLogin = async (e) => {
    e.preventDefault();
    setLoginBusy(true);
    try {
      await api.post("/auth/login-jpa", { jpa_id: loginJpaId, password: loginPass });
      await refreshMe();  // AuthContext refreshes; useEffect will then load info
      toast.success("Welcome!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setLoginBusy(false); }
  };

  const selectedList = useMemo(() => Object.entries(picks).map(([k, v]) => {
    const [ec, ac] = k.split("|");
    const row = info?.matrix.find((m) => m.event_category === ec && m.age_category === ac);
    return { key: k, ec, ac, row, ...v };
  }), [picks, info]);
  const total = useMemo(() => selectedList.reduce((s, x) => s + (x.row?.fee || 0), 0), [selectedList]);

  const togglePick = (row) => {
    const k = `${row.event_category}|${row.age_category}`;
    setPicks((p) => { const n = { ...p }; if (n[k]) delete n[k]; else n[k] = {}; return n; });
  };
  const setPartner = (k, v) => setPicks((p) => ({ ...p, [k]: { ...p[k], partner_jpa_id: v } }));

  const onReceipt = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Receipt must be under 3MB"); return; }
    const raw = await fileToDataUrl(f);
    setReceipt(await compressImg(raw));
  };

  const submit = async () => {
    if (!whatsapp) { toast.error("WhatsApp number required"); return; }
    if (selectedList.length === 0) { toast.error("Pick at least one category"); return; }
    for (const s of selectedList) {
      if (s.row?.is_doubles && !s.partner_jpa_id) { toast.error(`Enter partner JPA ID for ${s.row.event_label}`); return; }
    }
    setSubmitting(true);
    try {
      const payload = {
        whatsapp_number: whatsapp,
        receipt_data_url: receipt,
        total_amount: total,
        picks: selectedList.map((s) => ({ event_category: s.ec, age_category: s.ac, partner_jpa_id: s.partner_jpa_id || null })),
      };
      const r = await api.post(`/tournaments/${tid}/join`, payload);
      setResult(r.data);
      setStep(5);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSubmitting(false); }
  };

  // Step 0 — inline login gate (JPA ID + password only; no email, no Google)
  if (!user || step === 0) {
    return (
      <div data-testid="join-login-gate" className="max-w-md mx-auto px-4 md:px-8 py-16">
        <Link to={`/tournaments/${tid}`} className="text-xs uppercase tracking-widest text-jpa-primary">← Back to tournament</Link>
        <h1 className="font-display text-4xl tracking-wider mt-3">JOIN TOURNAMENT</h1>
        <p className="text-sm text-jpa-muted mt-1">Log in with your JPA ID to continue.</p>
        <form onSubmit={doJpaLogin} className="mt-6 border border-jpa-border rounded-sm p-6 space-y-4">
          <div>
            <Label>JPA Registration ID *</Label>
            <Input data-testid="join-login-jpa" required value={loginJpaId} onChange={(e) => setLoginJpaId(e.target.value.toUpperCase())} placeholder="e.g. JPA26000001" className="rounded-sm mt-1 font-mono" />
          </div>
          <div>
            <Label>Password *</Label>
            <PasswordInput data-testid="join-login-password" toggleTestId="join-login-password-toggle" required value={loginPass} onChange={(e) => setLoginPass(e.target.value)} className="rounded-sm mt-1" />
          </div>
          <Button data-testid="join-login-submit" type="submit" disabled={loginBusy} className="w-full bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest font-bold rounded-sm h-12">
            {loginBusy ? "Signing in..." : "Login & Continue"}
          </Button>
        </form>
        <div className="mt-5 text-center text-sm text-jpa-muted">
          Not a JPA member yet? <Link data-testid="join-register-now-link" to="/register" className="text-jpa-primary font-bold uppercase tracking-widest">Register now</Link>
        </div>
      </div>
    );
  }

  if (!info) return <div className="py-24 text-center text-jpa-muted">Loading…</div>;
  const t = info.tournament;
  const alreadyKeys = new Set((info.already_registered || []).map((r) => `${r.event_category}|${r.age_category}`));

  if (step === 5 && result) {
    const orgPhone = ""; // we don't have organizer phone in the info payload
    const waMsg = encodeURIComponent(result.whatsapp_prefill || "");
    return (
      <div data-testid="join-thank-you" className="max-w-xl mx-auto px-4 md:px-8 py-24 text-center">
        <CheckCircle2 className="w-16 h-16 text-jpa-primary mx-auto mb-6" />
        <h1 className="font-display text-4xl tracking-wider mb-3">REQUEST RECEIVED</h1>
        <p className="text-jpa-muted mb-2">Your Request Has been Received, we will send you the confirmation E-mail within 24 hours.</p>
        <p className="text-sm text-jpa-muted mb-8">Total paid: <b className="text-jpa-ink">₹{result.total_amount}</b> · {result.entries_created} event(s)</p>
        <div className="flex flex-col gap-3">
          <a data-testid="join-whatsapp-btn" href={`https://wa.me/?text=${waMsg}`} target="_blank" rel="noreferrer">
            <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white uppercase tracking-widest font-bold rounded-sm">
              <MessageCircle className="w-4 h-4 mr-2" /> Confirm via WhatsApp
            </Button>
          </a>
          <Link to="/profile"><Button variant="outline" className="w-full rounded-sm uppercase tracking-widest text-xs font-bold">Go to my profile</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="join-tournament-page" className="max-w-4xl mx-auto px-4 md:px-8 py-8">
      <Link to={`/tournaments/${tid}`} className="text-xs uppercase tracking-widest text-jpa-primary">← Back to tournament</Link>
      <h1 className="font-display text-3xl md:text-4xl tracking-wider mt-2">JOIN — {t.name.toUpperCase()}</h1>
      <div className="text-jpa-muted text-sm mb-6">{t.location} · {t.start_date} → {t.end_date}{t.organizer_club_name ? ` · By ${t.organizer_club_name}` : ""}</div>

      <div className="flex items-center gap-4 mb-8 flex-wrap">
        <StepDot n={1} label="Details" active={step >= 1} />
        <ChevronRight className="w-4 h-4 text-jpa-muted" />
        <StepDot n={2} label="Categories" active={step >= 2} />
        <ChevronRight className="w-4 h-4 text-jpa-muted" />
        <StepDot n={3} label="Payment" active={step >= 3} />
        <ChevronRight className="w-4 h-4 text-jpa-muted" />
        <StepDot n={4} label="Confirm" active={step >= 4} />
      </div>

      {step === 1 && (
        <div className="border border-jpa-border rounded-sm p-6 space-y-4">
          <h3 className="font-display text-2xl tracking-wider">Your details</h3>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <div><Label>Name</Label><Input value={info.player.name} disabled className="rounded-sm mt-1" /></div>
            <div><Label>JPA ID</Label><Input value={info.player.jpa_id} disabled className="rounded-sm mt-1" /></div>
            <div><Label>Gender</Label><Input value={info.player.gender} disabled className="rounded-sm mt-1" /></div>
            <div><Label>DOB</Label><Input value={info.player.dob} disabled className="rounded-sm mt-1" /></div>
            <div className="md:col-span-2">
              <Label>WhatsApp number *</Label>
              <Input data-testid="join-whatsapp-input" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="+91 90000 00000" className="rounded-sm mt-1" />
              <div className="text-[10px] text-jpa-muted mt-1">Organizer will send payment confirmation to this number.</div>
            </div>
          </div>
          <Button data-testid="join-next-1" disabled={!whatsapp} onClick={() => setStep(2)} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest text-xs font-bold">Next →</Button>
        </div>
      )}

      {step === 2 && (
        <div className="border border-jpa-border rounded-sm p-6 space-y-3">
          <h3 className="font-display text-2xl tracking-wider">Choose categories</h3>
          <p className="text-sm text-jpa-muted">Eligible categories are shown with the entry fee.  Non-eligible categories are grayed out — hover for reason.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {info.matrix.map((row) => {
              const k = `${row.event_category}|${row.age_category}`;
              const already = alreadyKeys.has(k);
              const disabled = !row.eligible || already;
              const selected = !!picks[k];
              return (
                <div key={k} data-testid={`cat-${row.event_category}-${row.age_category}`}
                  title={row.ineligible_reason || (already ? "You are already registered in this category" : "")}
                  className={`border rounded-sm p-3 flex items-center justify-between gap-3 ${disabled ? "opacity-50 bg-jpa-surface cursor-not-allowed" : selected ? "border-jpa-primary bg-jpa-primary/5 cursor-pointer" : "border-jpa-border cursor-pointer hover:border-jpa-primary"}`}
                  onClick={() => !disabled && togglePick(row)}>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm">{row.event_label}</div>
                    <div className="text-xs text-jpa-muted">{row.age_label}</div>
                    {row.ineligible_reason && <div className="text-[10px] text-jpa-live mt-0.5">{row.ineligible_reason}</div>}
                    {already && <div className="text-[10px] text-emerald-700 mt-0.5">✓ Already registered</div>}
                  </div>
                  <div className="text-right">
                    <div className="font-display text-xl tracking-wider">₹{row.fee}</div>
                    {selected && <Badge className="bg-jpa-primary text-white rounded-sm text-[9px] mt-1">Selected</Badge>}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Partner JPA ID for doubles picks */}
          {selectedList.filter((s) => s.row?.is_doubles).map((s) => (
            <div key={s.key} className="border border-jpa-border rounded-sm p-3">
              <Label className="text-xs">Partner JPA ID — {s.row.event_label} · {s.row.age_label} *</Label>
              <Input data-testid={`partner-${s.ec}-${s.ac}`} value={s.partner_jpa_id || ""} onChange={(e) => setPartner(s.key, e.target.value.toUpperCase())} placeholder="e.g. JPA26000123" className="rounded-sm mt-1" />
            </div>
          ))}
          <div className="flex items-center justify-between pt-3 border-t border-jpa-border">
            <Button variant="outline" onClick={() => setStep(1)} className="rounded-sm text-xs uppercase tracking-widest"><ChevronLeft className="w-3 h-3 mr-1" /> Back</Button>
            <div className="text-sm">Total: <b className="font-display text-2xl tracking-wider ml-2">₹{total}</b></div>
            <Button data-testid="join-next-2" disabled={selectedList.length === 0} onClick={() => setStep(3)} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest text-xs font-bold">Next →</Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="border border-jpa-border rounded-sm p-6 space-y-4">
          <h3 className="font-display text-2xl tracking-wider">Payment · ₹{total}</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-primary mb-2">Scan & pay</div>
              {t.payment_qr_data_url ? (
                <div className="border border-jpa-border p-3 rounded-sm bg-white inline-block">
                  <img src={t.payment_qr_data_url} alt="Payment QR" className="w-64 h-64 object-contain" data-testid="join-qr-image" />
                </div>
              ) : (
                <div className="text-sm text-jpa-muted border border-dashed border-jpa-border p-6 rounded-sm">Organizer has not uploaded a QR. Please contact them for payment instructions.</div>
              )}
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-primary mb-2">Upload payment receipt *</div>
              <label className="flex items-center gap-3 border-2 border-dashed border-jpa-border rounded-sm p-4 cursor-pointer hover:border-jpa-primary transition-colors">
                <UploadCloud className="w-5 h-5 text-jpa-primary" />
                <span className="text-sm text-jpa-muted">{receipt ? "Receipt uploaded — click to replace" : "Click to upload receipt image (up to 3MB)"}</span>
                <input data-testid="join-receipt-upload" type="file" accept="image/*" className="hidden" onChange={onReceipt} />
              </label>
              {receipt && (
                <div className="mt-3 relative w-40 border border-jpa-border">
                  <img src={receipt} alt="Receipt" className="w-full h-auto" />
                  <button onClick={() => setReceipt(null)} className="absolute -top-2 -right-2 bg-jpa-live text-white rounded-full p-1"><X className="w-3 h-3" /></button>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-between pt-3 border-t border-jpa-border">
            <Button variant="outline" onClick={() => setStep(2)} className="rounded-sm text-xs uppercase tracking-widest"><ChevronLeft className="w-3 h-3 mr-1" /> Back</Button>
            <Button data-testid="join-next-3" disabled={!receipt} onClick={() => setStep(4)} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest text-xs font-bold">Review →</Button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="border border-jpa-border rounded-sm p-6 space-y-3">
          <h3 className="font-display text-2xl tracking-wider">Review & confirm</h3>
          <div className="text-sm">
            <div><b>Name:</b> {info.player.name} · <b>JPA ID:</b> {info.player.jpa_id}</div>
            <div><b>WhatsApp:</b> {whatsapp}</div>
          </div>
          <div className="border border-jpa-border rounded-sm">
            {selectedList.map((s) => (
              <div key={s.key} className="grid grid-cols-12 py-2 px-3 border-b border-jpa-border last:border-b-0 text-sm">
                <div className="col-span-6">{s.row?.event_label} · {s.row?.age_label}{s.partner_jpa_id ? ` · w/ ${s.partner_jpa_id}` : ""}</div>
                <div className="col-span-6 text-right font-bold">₹{s.row?.fee}</div>
              </div>
            ))}
            <div className="grid grid-cols-12 py-2 px-3 bg-jpa-ink text-white">
              <div className="col-span-6 font-bold uppercase tracking-widest text-xs">Total</div>
              <div className="col-span-6 text-right font-display text-2xl tracking-wider">₹{total}</div>
            </div>
          </div>
          <div className="flex justify-between pt-3 border-t border-jpa-border">
            <Button variant="outline" onClick={() => setStep(3)} className="rounded-sm text-xs uppercase tracking-widest"><ChevronLeft className="w-3 h-3 mr-1" /> Back</Button>
            <Button data-testid="join-submit" disabled={submitting} onClick={submit} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest text-xs font-bold">{submitting ? "Submitting…" : "Submit registration"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
